import { z } from "zod";

export const memberRequestStatusSchema = z.enum(["pending", "approved", "rejected", "cancelled", "revoked"]);
export type MemberRequestStatus = z.infer<typeof memberRequestStatusSchema>;
export const memberApprovalPolicySchema = z.object({
  version: z.number().int().nonnegative(),
  enabled: z.boolean(),
  reviewer_admin_ids: z.array(z.string().uuid()),
});
export type MemberApprovalPolicy = z.infer<typeof memberApprovalPolicySchema>;
export const memberRequestSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  email: z.string(),
  display_name: z.string(),
  status: memberRequestStatusSchema,
  requested_at: z.iso.datetime({ offset: true }),
  decided_at: z.iso.datetime({ offset: true }).nullable(),
  decided_by: z.string().uuid().nullable(),
  decided_by_name: z.string().optional(),
  decided_by_email: z.string().optional(),
  decision_note: z.string(),
  revoked_at: z.iso.datetime({ offset: true }).nullable(),
  revoked_by: z.string().uuid().nullable(),
  revoked_by_name: z.string().optional(),
  revoked_by_email: z.string().optional(),
  revocation_note: z.string(),
});
export type MemberRequest = z.infer<typeof memberRequestSchema>;
export const memberRequestsSchema = z.object({
  items: z.array(memberRequestSchema),
  next_cursor: z.string().uuid().nullable().optional(),
});
export const memberApprovalSearchSchema = z.object({
  memberStatus: memberRequestStatusSchema.optional(),
  memberCursor: z.string().uuid().optional(),
});
export const memberReviewersSchema = z.object({ items: z.array(z.object({ id: z.string().uuid(), name: z.string(), email: z.string() })) });
export type MemberReviewer = z.infer<typeof memberReviewersSchema>["items"][number];
export const memberDecisionSchema = z.enum(["approve", "reject", "revoke"]);
export type MemberDecision = z.infer<typeof memberDecisionSchema>;
export const memberDecisionInputSchema = z.object({
  requestId: z.string().uuid(), decision: memberDecisionSchema,
  note: z.string().refine((value) => new TextEncoder().encode(value).length <= 500 && !value.includes("\0"), "Shorten the note and remove unsupported characters."),
});
export const memberPolicyInputSchema = memberApprovalPolicySchema.superRefine((policy, context) => {
  if (policy.reviewer_admin_ids.length > 20 || new Set(policy.reviewer_admin_ids).size !== policy.reviewer_admin_ids.length) context.addIssue({ code: "custom", message: "Choose up to 20 distinct reviewers.", path: ["reviewer_admin_ids"] });
});

// The membership desk has its own workflow and authorization. It does not add
// membership to the access/recovery engine's closed request-kind union.
export const memberDeskSearchSchema = z.object({
  status: memberRequestStatusSchema.optional(),
  organization_id: z.string().uuid().optional(),
  request_id: z.string().uuid().optional(),
  cursor: z.string().uuid().optional(),
});
export type MemberDeskSearch = z.infer<typeof memberDeskSearchSchema>;
export const memberDeskRequestSchema = memberRequestSchema.extend({
  organization_id: z.string().uuid(),
  organization_name: z.string(),
  approval_enabled: z.boolean(),
});
export type MemberDeskRequest = z.infer<typeof memberDeskRequestSchema>;
export const memberDeskResponseSchema = z.object({ items: z.array(memberDeskRequestSchema), next_cursor: z.string().uuid().nullable().optional() });
